import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackgroundPattern from './BackgroundPattern';
import { API_URL } from '../utils/Functions';

const Services = ({ onOpenConsultation }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/public/services?active=1`)
      .then(res => res.json())
      .then(data => {
        setServices(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching services:', err);
        setLoading(false);
      });
  }, []);

  return (
    <section id="services" className="bg-light py-5 position-relative overflow-hidden">
      <BackgroundPattern position="right" top="30%" />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        
        .svc-card {
          background: #fff;
          border-radius: 20px;
          border: 1px solid #e8edf3;
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
          font-family: 'Inter', sans-serif;
        }

        .svc-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(6,24,48,0.08);
          border-color: transparent;
        }

        .svc-img-wrap {
          width: 100%;
          height: 180px;
          overflow: hidden;
          position: relative;
          background: #f1f5f9;
        }

        .svc-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .svc-card:hover .svc-img-wrap img {
          transform: scale(1.08);
        }

        .svc-content {
          padding: 28px 24px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .svc-title {
          font-size: 17px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 12px;
          line-height: 1.3;
        }

        .svc-desc {
          font-size: 14px;
          color: #64748b;
          line-height: 1.65;
          margin-bottom: 24px;
          flex-grow: 1;
        }

        .svc-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13.5px;
          font-weight: 700;
          color: #CFA047;
          text-decoration: none;
          transition: gap 0.2s, color 0.2s;
          margin-top: auto;
        }

        .svc-link:hover {
          gap: 10px;
          color: #a67f35;
        }

        /* Modal Styles */
        .svc-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(6, 24, 48, 0.6);
          backdrop-filter: blur(4px);
          z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          opacity: 0; animation: svcFadeIn 0.2s forwards;
        }
        @keyframes svcFadeIn { to { opacity: 1; } }
        
        .svc-modal-content {
          background: #fff;
          border-radius: 24px;
          width: 100%; max-width: 600px;
          max-height: 90vh; overflow-y: auto;
          box-shadow: 0 32px 80px rgba(0,0,0,0.2);
          position: relative;
          transform: translateY(20px); animation: svcSlideUp 0.3s forwards ease-out;
        }
        @keyframes svcSlideUp { to { transform: translateY(0); } }

        .svc-modal-close {
          position: absolute; top: 20px; right: 20px;
          background: rgba(255,255,255,0.9); border: none;
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #0f172a; cursor: pointer; z-index: 10;
          transition: background 0.2s; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .svc-modal-close:hover { background: #f8fafc; color: #ef4444; }

        .svc-modal-img {
          width: 100%; height: 260px; object-fit: cover;
          background: #f1f5f9;
        }
        
        .svc-modal-body { padding: 36px; }
        .svc-modal-title { font-size: 26px; font-weight: 800; color: #0f172a; margin-bottom: 16px; letter-spacing: -0.5px; }
        .svc-modal-desc { font-size: 15px; color: #475569; line-height: 1.7; margin-bottom: 32px; white-space: pre-wrap; }
        
        .svc-modal-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          padding: 16px 36px; width: 100%;
          background: linear-gradient(135deg, #061830 0%, #0a2540 100%);
          color: #fff; border: none; border-radius: 12px;
          font-size: 16px; font-weight: 700; cursor: pointer;
          transition: all 0.2s; box-shadow: 0 8px 24px rgba(6,24,48,0.2);
        }
        .svc-modal-btn:hover {
          transform: translateY(-2px);
          background: linear-gradient(135deg, #CFA047 0%, #b8860b 100%);
          box-shadow: 0 12px 32px rgba(207,160,71,0.3); color: #fff;
        }
      `}</style>

      <div className="container py-5 position-relative z-1">

        {/* ── Top Header ── */}
        <div className="text-center mb-5 pb-3" style={{ maxWidth: '720px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
          <div
            className="d-inline-flex align-items-center gap-2 mb-3 px-4 py-2 rounded-pill shadow-sm"
            style={{
              background: '#fff',
              border: '1px solid #e8edf3',
              fontSize: '12.5px',
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: '#CFA047'
            }}
          >
            <span style={{ width: 8, height: 8, background: '#CFA047', borderRadius: '50%', boxShadow: '0 0 8px rgba(207,160,71,0.5)' }}></span>
            What We Offer
          </div>

          <h2 className="display-4 fw-bold mb-4" style={{ color: '#0f172a', letterSpacing: '-1.5px' }}>
            Our <span style={{ color: '#CFA047' }}>Services</span>
          </h2>

          <p className="text-muted" style={{ fontSize: '17px', lineHeight: 1.8 }}>
            Expert tax and financial services tailored for small businesses, entrepreneurs, and
            individuals — all under one roof to maximize your returns and ensure compliance.
          </p>
        </div>

        {/* ── Cards Grid (4 per row) ── */}
        <div className="row g-4 justify-content-center">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border" style={{ color: '#CFA047' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : services.length === 0 ? (
            <div className="col-12 text-center text-muted py-5">
              No services found.
            </div>
          ) : (
            services.map((svc, i) => (
              <div key={svc.id || i} className="col-lg-3 col-md-6 col-sm-6 d-flex">
                <div 
                  className="svc-card w-100" 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => {
                    if (svc.page && svc.page.slug) {
                      navigate(`/pages/${svc.page.slug}`);
                    } else {
                      setSelectedService(svc);
                    }
                  }}
                >

                  {/* Image Top */}
                  <div className="svc-img-wrap d-flex align-items-center justify-content-center" style={{ background: svc.image ? '#f1f5f9' : '#e2e8f0' }}>
                    {svc.image ? (
                      <img src={svc.image} alt={svc.title} loading="lazy" />
                    ) : (
                      <i className="fa-solid fa-briefcase" style={{ fontSize: '48px', color: '#94a3b8' }}></i>
                    )}
                  </div>

                  {/* Content Bottom */}
                  <div className="svc-content">
                    <h5 className="svc-title">{svc.title}</h5>
                    <p className="svc-desc">
                      {svc.description
                        ? (svc.description.length > 100 ? svc.description.slice(0, 100) + '...' : svc.description)
                        : 'No description available.'}
                    </p>

                    <div className="svc-link">
                      View Details <i className="fa-solid fa-arrow-right"></i>
                    </div>
                  </div>

                </div>
              </div>
            ))
          )}
        </div>

      </div>

      {/* ── Modal ── */}
      {selectedService && (
        <div className="svc-modal-overlay" onClick={() => setSelectedService(null)}>
          <div className="svc-modal-content" onClick={e => e.stopPropagation()}>
            <button className="svc-modal-close" onClick={() => setSelectedService(null)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
            
            {selectedService.image ? (
              <img src={selectedService.image} alt={selectedService.title} className="svc-modal-img" />
            ) : (
              <div className="svc-modal-img d-flex align-items-center justify-content-center">
                <i className="fa-solid fa-briefcase" style={{ fontSize: '72px', color: '#94a3b8' }}></i>
              </div>
            )}
            
            <div className="svc-modal-body">
              <h3 className="svc-modal-title">{selectedService.title}</h3>
              <div className="svc-modal-desc">{selectedService.description || 'No description available.'}</div>
              
              <button 
                className="svc-modal-btn"
                onClick={() => {
                  if (onOpenConsultation) {
                    onOpenConsultation(selectedService.title);
                  } else {
                    navigate(`/contact?service=${encodeURIComponent(selectedService.title)}`);
                  }
                  setSelectedService(null);
                }}
              >
                Book Now <i className="fa-solid fa-calendar-check"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;
